import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Alert,
} from "@mui/material";
import GridOnIcon from "@mui/icons-material/GridOn";
import { sourceLabel, RECORD_LIST_PATH } from "../fieldCatalog";
import {
  loadRecordOptions,
  loadRecord,
  loadRecordsInRange,
  getSourceConfig,
} from "../../../services/ReportRecordLoader";
import { generateExcel, generateExcelForMany } from "../render/excelRenderer";

export default function GenerateReportDialog({ open, onClose, template }) {
  const source = template.source;
  const cfg = getSourceConfig(source);
  const hasDate = !!(cfg && cfg.dateField);
  const isListTemplate = (template.sections || []).some(
    (s) => s.type === "DATA_TABLE" && s.collectionPath === RECORD_LIST_PATH
  );

  const [mode, setMode] = useState("single");
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError("");
    setPicked(null);
    setLoadingOptions(true);
    (async () => {
      const opts = await loadRecordOptions(source);
      if (cancelled) return;
      setOptions(opts);
      setLoadingOptions(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, source]);

  const generateSingle = async () => {
    if (!picked) return;
    setBusy(true);
    setError("");
    const record = await loadRecord(source, picked.id);
    if (record) {
      await generateExcel(template, record);
      onClose();
    } else {
      setError("Could not load that record.");
    }
    setBusy(false);
  };

  const generateBulk = async () => {
    setBusy(true);
    setError("");
    setProgress({ done: 0, total: 0 });
    const records = await loadRecordsInRange(source, start || null, end || null, (done, total) =>
      setProgress({ done, total })
    );
    if (records.length) {
      await generateExcelForMany(template, records);
      onClose();
    } else {
      setError("No records found for the selected range.");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate report — {sourceLabel(source)}</DialogTitle>
      <DialogContent dividers>
        {isListTemplate ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              List report — one row per {sourceLabel(source)}. All matching records are written into a single sheet.
            </Alert>
            {hasDate ? (
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Every record will be included (this source has no date field to filter on).
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, v) => v && setMode(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="single">Single record</ToggleButton>
              <ToggleButton value="bulk">Date range / all</ToggleButton>
            </ToggleButtonGroup>

            {mode === "single" ? (
              <Autocomplete
                options={options}
                value={picked}
                loading={loadingOptions}
                onChange={(_, opt) => setPicked(opt)}
                getOptionLabel={(o) => (o && o.label) || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Choose a record" autoFocus />}
              />
            ) : (
              <Box>
                {hasDate ? (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      type="date"
                      label="From"
                      size="small"
                      value={start}
                      onChange={(e) => setStart(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      type="date"
                      label="To"
                      size="small"
                      value={end}
                      onChange={(e) => setEnd(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Box>
                ) : (
                  <Alert severity="info">
                    This source has no date field — every record will be included in one workbook.
                  </Alert>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Each record is stacked into the same sheet using this template.
                </Typography>
              </Box>
            )}
          </>
        )}

        {busy && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant={progress.total ? "determinate" : "indeterminate"}
              value={progress.total ? (progress.done / progress.total) * 100 : undefined}
            />
            {progress.total > 0 && (
              <Typography variant="caption" color="text.secondary">
                Loading {progress.done} / {progress.total} records…
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<GridOnIcon />}
          disabled={busy || (!isListTemplate && mode === "single" && !picked)}
          onClick={isListTemplate || mode === "bulk" ? generateBulk : generateSingle}
        >
          Generate .xlsx
        </Button>
      </DialogActions>
    </Dialog>
  );
}
